import { DraggableProvided } from '@hello-pangea/dnd'
import type { MultipleChoiceItem } from '@vedalib/editor/lib/elements'
import { TestAnswerFeedbackDisplayOption } from '@vedalib/editor/lib/elements'
import { textToRtValue } from '@vedalib/rich-text'
import type { RichTextValue } from '@vedalib/rich-text'
import cn from 'classnames'
import * as R from 'ramda'

import TestFeedback from 'components/Editor/EditorElements/courseKit/TestFeedback'
import {
  getFeedbackValue,
  onChangeRTOptionFb,
} from 'components/Editor/EditorElements/utils/getFeedbackValue'
import AbsolutePortal from 'components/Editor/components/AbsolutePortal/AbsolutePortal'
import RichText from 'components/form/RichText/RichText'
import { useGetRichTextProps } from 'components/form/RichText/useGetRichTextProps'
import { IconButton } from 'components/uiKit/Button'
import { Tooltip } from 'components/uiKit/Dropdown'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { PreviewMode } from 'services/Store/Project/enums'
import { t } from 'services/Translation'
import { extendTestData } from 'utils/test/qaData'

import type { TestMultipleChoiceElementType } from '../testMultipleChoiceTypes'
import Checkbox from './Checkbox'
import * as s from './TestMultipleChoiceElement.module.scss'

interface ITestMultipleChoiceElementEditorItemProps
  extends Pick<
    React.ComponentProps<TestMultipleChoiceElementType>,
    'element' | 'mode' | 'styles' | 'waiting' | 'onChange' | 'font' | 'feedback'
  > {
  index: number
  provided: DraggableProvided
  showControls?: boolean
}

const TestMultipleChoiceElementEditorItem: React.FC<ITestMultipleChoiceElementEditorItemProps> = ({
  showControls,
  provided,
  index,
  element,
  mode,
  styles,
  waiting,
  onChange,
  font,
  feedback,
}) => {
  const { id: elementId, value } = element
  const { items } = value

  const { isActiveElement, activeIndex, onLabelSelect, isFill } = useGetRichTextProps({
    elementId,
    mode,
  })
  const feedbackValue = getFeedbackValue(value)

  const onChangeLabel = (labelValue: RichTextValue, index: number) =>
    onChange?.(R.assocPath(['items', index, 'label'], labelValue, value))

  const handleDelete = (item: MultipleChoiceItem) => () =>
    onChange?.(
      R.assoc(
        'items',
        items.filter(({ value: id }) => id !== item.value),
        value,
      ),
    )

  const handleSetValid = (index: number, isValid: boolean) => () =>
    onChange?.(R.assocPath(['items', index, 'isValid'], isValid, value))

  return (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      className={cn(s.wrapItem, { [s.active]: activeIndex === index.toString() })}
      key={items[index].value}
      style={{ ...provided.draggableProps.style }}
    >
      <TestFeedback
        feedbackValue={feedbackValue}
        font={font}
        isTrueAnswer={items[index]?.isValid}
        mode={mode}
        onChange={onChangeRTOptionFb(index, value, onChange)}
        rtProps={{
          activeIndex,
          isActiveElement,
          onLabelSelect,
          isFill,
          value: items[index].feedback,
        }}
        showFeedback={
          value.displayFeedback && value.displayOption !== TestAnswerFeedbackDisplayOption.general
        }
        styles={feedback}
        showAnswerFb
      >
        <Checkbox
          checked={items[index].isValid}
          setIsValid={handleSetValid(index, !items[index].isValid)}
          styles={styles}
          testData={extendTestData({
            label: items[index]?.label
              ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (items[index]?.label[0] as any).children[0].text
              : '',
            index: index,
          })}
          value={items[index].value}
          isEditor
        >
          <span onMouseDown={() => onLabelSelect(index.toString())}>
            <RichText
              active={isActiveElement && activeIndex === index.toString()}
              disabled={!isFill}
              isPreview={mode?.previewMode !== PreviewMode.editor}
              name={`checkboxItem.${index}`}
              onChange={(val) => onChangeLabel(val, index)}
              styles={font}
              value={items[index]?.label || textToRtValue('')}
              waiting={waiting}
            />
          </span>
        </Checkbox>
        <div
          {...provided.dragHandleProps}
          className={cn(s.dnd, isFill && isActiveElement && s.show)}
        >
          <Icon name='builderDrugAndDrop' size={KitSize.XS} />
        </div>
        {isFill && isActiveElement && showControls && (
          <AbsolutePortal
            name='deleteMultipleChoiceItem'
            placement='right'
            translateX='3px'
            translateY='-50%'
          >
            <Tooltip overlay={t('uiKit.tooltip.deleteAnswer')}>
              <IconButton
                disabled={items.length < 3}
                icon='otherClose'
                name='deleteMultipleChoiceItem'
                onClick={handleDelete(items[index])}
                size={KitSize.XS}
                styleType='secondary'
              />
            </Tooltip>
          </AbsolutePortal>
        )}
      </TestFeedback>
    </div>
  )
}

export default TestMultipleChoiceElementEditorItem
